import React, { useEffect, useState, useLayoutEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ButtonToolbar, Card, Row, Col } from 'react-bootstrap'
import { IQuestionType } from 'models/questionTypes.model'
import { IMainState } from 'store/states'
import * as fromQuestionTypesActions from 'store/actions/questionsTypes.action'
import SelectInput from 'components/ui/forms/field/select-input/SelectInput'
import {
  prepareLikertQuestionData,
  prepareOpenEndedData,
} from 'helpers/converters.helper'
import CheckBoxRadioButtonInput from 'components/ui/forms/field/checkbox-radiobutton/CheckBoxRadioButtonInput'
import { QuestionTypeEnum } from 'enum'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import { IQuestionData } from 'models/question.model'
import { ILikerOptionModel } from 'models/questionLikert.model'
import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import LikertQuestion from './components/likert-question/LikertQuestion'
import styles from './SurveyQuestionsEditor.module.scss'

interface ISurveyQuestionsEditor {
  index: number
  data?: IQuestionData
  blockId: number | undefined
  onSave: any
  id?: number
  onDiscard?: () => void
  sQuestionData?: any
  sAnswerData?: any
}

const questionDefaultValues = {
  name: '',
  mandatory: false,
  type: { value: '', label: '' },
}

const likertAnswerDefaultValues: ILikerOptionModel[] = [
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
  { label: '' },
]

const SurveyQuestionsEditor: React.FC<ISurveyQuestionsEditor> = ({
  data = undefined,
  index,
  blockId,
  onSave,
  onDiscard = undefined,
  sQuestionData,
  sAnswerData,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { types } = useSelector(
    ({ questionTypes }: IMainState) => questionTypes
  )
  const { loading } = useSelector(({ questions }: IMainState) => questions)

  const [questionData, setQuestionData] = useState(questionDefaultValues)
  const [answerData, setAnswerData] = useState<ILikerOptionModel[]>(
    likertAnswerDefaultValues
  )

  const [editing, setEditing] = useState(false)

  useLayoutEffect(() => {
    dispatch({ type: fromQuestionTypesActions.GET_QUESTION_TYPES_LIST })
  }, [dispatch])

  const questionTypes = useMemo(
    () =>
      types.map((type: IQuestionType) => {
        return {
          ...type,
          label: t(`administration.blocks.type.survey.question.${type.label}`),
        }
      }),
    [t, types]
  )

  useEffect(() => {
    if (data) {
      const translateKeys = {
        [QuestionTypeEnum.likert]: 'likert',
        [QuestionTypeEnum.openEnded]: 'openEnded',
      }

      const questionableType = data.questionableType
      const translateKey = translateKeys[questionableType]
      const prepareData = {
        ...data,
        type: {
          label: t(
            `administration.blocks.type.survey.question.${translateKey}`
          ),
          value: questionableType,
        },
        id: data.questionableId,
      }

      setQuestionData(prepareData)
      setAnswerData((current) => {
        const options = [...current]
        data.options.map((option: any, index: number) => {
          options[index] = option
        })
        return options
      })
      setEditing(true)
    }
  }, [data, t])

  const handleOnSave = async () => {
    let data: any
    if (questionData['name'] && questionData.type.label !== '') {
      switch (questionData.type.value) {
        case QuestionTypeEnum.likert:
          data = prepareLikertQuestionData(
            questionData,
            answerData,
            index,
            blockId
          )
          await onSave(data, questionData.type.value)
          break
        case QuestionTypeEnum.openEnded:
          data = prepareOpenEndedData(questionData, index, blockId)
          await onSave(data, questionData.type.value)
          break
        default:
      }
    }
  }

  const handleDiscard = () => {
    if (onDiscard) onDiscard()
    else {
      setQuestionData(questionDefaultValues)
      setAnswerData(likertAnswerDefaultValues)
    }
  }

  useEffect(() => {
    const textarea = document.getElementById('text-area')
    if (textarea) {
      textarea.style.height = '0px'
      const height =
        textarea.scrollHeight > 54
          ? 54
          : textarea.scrollHeight < 30
          ? 30
          : textarea.scrollHeight
      textarea.style.height = height + 'px'
    }
  }, [questionData])

  useEffect(() => {
    sAnswerData(answerData)
    sQuestionData(questionData)
  }, [answerData, questionData, sAnswerData, sQuestionData])

  return (
    <>
      <Card className={'d-flex flex-column p-3'}>
        {loading ? (
          <Loader sizes={LoaderSizes.ExtraSmall} color />
        ) : (
          questionData && (
            <div>
              <h2 className={styles.title}>
                {' '}
                {`${t(
                  'administration.blocks.type.survey.question.singular'
                )} ${index}`}
              </h2>
              <textarea
                rows={1}
                name={'name'}
                id="text-area"
                value={questionData['name']}
                className={`w-100 form-control mb-2 `}
                style={{ resize: 'none', overflow: 'hidden', height: '33px' }}
                onChange={(e: any) =>
                  setQuestionData({ ...questionData, ['name']: e.target.value })
                }
              />

              <Row className={'d-flex mb-3 align-items-center'}>
                <Col md={6} lg={5} xl={4} className={styles.questionType}>
                  <SelectInput
                    name="questionType"
                    options={questionTypes}
                    closeMenuOnSelect={true}
                    value={questionData['type']}
                    isMulti={false}
                    withColors={false}
                    onChange={(selected: any) =>
                      setQuestionData({ ...questionData, ['type']: selected })
                    }
                    error={''}
                    disabled={editing}
                  />
                </Col>
                <Col>
                  <CheckBoxRadioButtonInput
                    key={blockId}
                    name={'mandatory-true'}
                    value={true}
                    checked={questionData['mandatory']}
                    inline={true}
                    type={'checkbox'}
                    label={t(
                      'administration.blocks.type.survey.question.mandatoryQuestion'
                    )}
                    disabled={questionData['type'].value === ''}
                    onChange={() =>
                      setQuestionData({
                        ...questionData,
                        ['mandatory']: !questionData['mandatory'],
                      })
                    }
                    error={''}
                  />
                </Col>
              </Row>
              {questionData['type'].value !== '' &&
                showSelectedQuestionTypeForm(
                  questionData['type'].value,
                  answerData,
                  setAnswerData
                )}
            </div>
          )
        )}
      </Card>
      <ButtonToolbar className="float-right my-3">
        <Button
          onClick={handleDiscard}
          variant={ButtonVariant.Secondary}
          size={ButtonSizes.Small}
        >
          {t('administration.blocks.type.survey.buttons.discard')}
        </Button>
        <Button
          size={ButtonSizes.Small}
          variant={ButtonVariant.Success}
          onClick={handleOnSave}
        >
          {t('administration.blocks.type.survey.buttons.save')}
        </Button>
      </ButtonToolbar>
    </>
  )
}

export default SurveyQuestionsEditor

const showSelectedQuestionTypeForm = (
  type: string,
  data: ILikerOptionModel[],
  onChange: any
) => {
  switch (type) {
    case 'Questions::Likert':
      return <LikertQuestion data={data} onChange={onChange} />
    // Las preguntas de tipo Questions::OpenEnded no retornan nada
    default:
      break
  }
}
